import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Maximize2, Minimize2, X } from '@digital-twins/design-system/icons'

import { Button } from '@/components/ui/button'
import { STUDY_EMBED_SOURCE } from '@/features/studies/embed/studyEmbedBridge'

/**
 * The study creator and its results, loaded in the thread pane.
 *
 * The shell stays one element when it expands, so the iframe is not remounted
 * and a study the frame already navigated does not reload. Width lives on the
 * sources drawer. This only consumes the embed bridge: launched, open-top,
 * and close-request / close-blocked.
 */

const DOCK_QUERY = '(min-width: 768px)'

/** Pages without the embed bridge never answer close-request. */
export const STUDY_FRAME_CLOSE_FALLBACK_MS = 300

interface StudyFramePaneProps {
  src: string
  onLaunched: (studyToken: string) => void
  onClose: () => void
  onBack: () => void
  /** Escape and the sheet close ask this pane to send close-request. */
  bindRequestClose?: (request: (() => void) | null) => void
}

interface LaunchedEmbedMessage {
  type: 'launched'
  studyToken: string
}

interface OpenTopEmbedMessage {
  type: 'open-top'
  href: string
}

interface CloseEmbedMessage {
  type: 'close-ok' | 'close-blocked'
}

export function studyFramePathMatches(
  currentPath: string | null,
  href: string,
): boolean {
  if (!currentPath) {
    return false
  }
  return currentPath === href
}

/** Null when the frame has not navigated to a real document yet. */
export function readStudyFramePath(frame: HTMLIFrameElement | null): string | null {
  if (!frame) {
    return null
  }
  try {
    const loc = frame.contentWindow?.location
    if (!loc || loc.href === 'about:blank' || !loc.pathname) {
      return null
    }
    return `${loc.pathname}${loc.search}`
  } catch {
    return null
  }
}

/** True when the frame document is already the href the parent would set. */
export function studyFrameAlreadyAt(
  frame: HTMLIFrameElement | null,
  href: string,
): boolean {
  return studyFramePathMatches(readStudyFramePath(frame), href)
}

function matchesDock(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(DOCK_QUERY).matches
  )
}

/** Same-origin path for the top window. Null when the href must leave the app. */
export function topWindowPath(href: string): string | null {
  try {
    const url = new URL(href, window.location.origin)
    if (url.origin !== window.location.origin) {
      return null
    }
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return null
  }
}

function readEmbedMessage(
  data: unknown,
): LaunchedEmbedMessage | OpenTopEmbedMessage | CloseEmbedMessage | null {
  if (!data || typeof data !== 'object') {
    return null
  }
  const record = data as {
    source?: unknown
    type?: unknown
    studyToken?: unknown
    href?: unknown
  }
  if (record.source !== STUDY_EMBED_SOURCE) {
    return null
  }
  if (
    record.type === 'launched' &&
    typeof record.studyToken === 'string' &&
    record.studyToken.trim()
  ) {
    return { type: 'launched', studyToken: record.studyToken }
  }
  if (record.type === 'open-top' && typeof record.href === 'string' && record.href) {
    return { type: 'open-top', href: record.href }
  }
  if (record.type === 'close-ok' || record.type === 'close-blocked') {
    return { type: record.type }
  }
  return null
}

export function StudyFramePane(props: StudyFramePaneProps) {
  const { src, onLaunched, onClose, onBack, bindRequestClose } = props

  const [expanded, setExpanded] = useState(false)
  const [docked, setDocked] = useState(matchesDock)
  const [appliedSrc, setAppliedSrc] = useState(src)
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const leaveIntent = useRef<'hide' | 'back'>('hide')
  const closeFallbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeFallbackFired = useRef(false)
  const onLaunchedRef = useRef(onLaunched)
  const onCloseRef = useRef(onClose)
  const onBackRef = useRef(onBack)
  const bindRequestCloseRef = useRef(bindRequestClose)
  onLaunchedRef.current = onLaunched
  onCloseRef.current = onClose
  onBackRef.current = onBack
  bindRequestCloseRef.current = bindRequestClose

  const { t } = useTranslation('app')
  const navigate = useNavigate()

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return
    }
    const query = window.matchMedia(DOCK_QUERY)
    const onChange = () => setDocked(query.matches)
    onChange()
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    if (studyFrameAlreadyAt(iframeRef.current, src)) {
      return
    }
    setAppliedSrc(src)
  }, [src])

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) {
        return
      }
      const frameWindow = iframeRef.current?.contentWindow
      if (!frameWindow || event.source !== frameWindow) {
        return
      }
      const message = readEmbedMessage(event.data)
      if (!message) {
        return
      }
      if (message.type === 'launched') {
        onLaunchedRef.current(message.studyToken)
        return
      }
      if (message.type === 'open-top') {
        const next = topWindowPath(message.href)
        if (next) {
          navigate(next)
          return
        }
        window.location.assign(message.href)
        return
      }
      clearCloseFallback()
      if (closeFallbackFired.current) {
        // The fallback already left. A late answer must not leave twice.
        closeFallbackFired.current = false
        return
      }
      if (message.type === 'close-blocked') {
        return
      }
      if (leaveIntent.current === 'back') {
        onBackRef.current()
        return
      }
      onCloseRef.current()
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [navigate])

  function clearCloseFallback() {
    if (closeFallbackTimer.current !== null) {
      clearTimeout(closeFallbackTimer.current)
      closeFallbackTimer.current = null
    }
  }

  useEffect(() => clearCloseFallback, [])

  function leaveNow(intent: 'hide' | 'back') {
    if (intent === 'back') {
      onBackRef.current()
      return
    }
    onCloseRef.current()
  }

  function requestLeave(intent: 'hide' | 'back') {
    leaveIntent.current = intent
    closeFallbackFired.current = false
    const frameWindow = iframeRef.current?.contentWindow
    if (!frameWindow) {
      if (intent === 'back') {
        onBackRef.current()
        return
      }
      onCloseRef.current()
      return
    }
    clearCloseFallback()
    closeFallbackTimer.current = setTimeout(() => {
      closeFallbackTimer.current = null
      closeFallbackFired.current = true
      leaveNow(leaveIntent.current)
    }, STUDY_FRAME_CLOSE_FALLBACK_MS)
    frameWindow.postMessage({ type: 'close-request' }, window.location.origin)
  }

  useEffect(() => {
    const bind = bindRequestCloseRef.current
    if (!bind) {
      return
    }
    bind(() => requestLeave('hide'))
    return () => {
      bindRequestCloseRef.current?.(null)
    }
    // requestLeave reads refs. Bind once for this mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleExpand() {
    setExpanded((current) => !current)
  }

  const showOverlay = expanded && docked
  const frameTitle = t('threads.sessions.studyFrameTitle')

  return (
    <div
      className={
        showOverlay
          ? 'fixed inset-3 z-50 flex min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-background shadow-lg'
          : 'flex min-h-0 flex-1 flex-col overflow-hidden'
      }
      data-testid={showOverlay ? 'thread-study-overlay' : 'thread-study-pane'}
      data-expanded={showOverlay ? 'true' : 'false'}
      role={showOverlay ? 'dialog' : undefined}
      aria-modal={showOverlay ? true : undefined}
      aria-label={showOverlay ? frameTitle : undefined}
    >
      <div className="flex shrink-0 items-center gap-1 border-b border-border p-2">
        <Button
          variant="ghost"
          size="sm"
          data-testid="thread-study-back"
          onClick={() => requestLeave('back')}
        >
          {t('threads.sessions.backToSessions')}
        </Button>
        <div className="min-w-0 flex-1" />
        {docked ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            data-testid="thread-study-expand"
            aria-pressed={showOverlay}
            aria-label={
              showOverlay
                ? t('threads.sessions.collapseStudy')
                : t('threads.sessions.expandStudy')
            }
            onClick={handleExpand}
          >
            {showOverlay ? (
              <Minimize2 className="h-4 w-4" aria-hidden />
            ) : (
              <Maximize2 className="h-4 w-4" aria-hidden />
            )}
          </Button>
        ) : null}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          data-testid="thread-session-hide"
          aria-label={t('threads.sessions.hide')}
          onClick={() => requestLeave('hide')}
        >
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </div>
      <iframe
        ref={iframeRef}
        title={frameTitle}
        src={appliedSrc}
        className="min-h-0 w-full flex-1 border-0 bg-background"
        data-testid="thread-study-frame"
      />
    </div>
  )
}
